import React, { useState, useEffect, useCallback } from 'react'
import api from '../../services/api'
import '../../css/playlist.css'

function fmtDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('it-IT')
}

export default function PlaylistPage() {
  const [backups, setBackups] = useState([])
  const [restoreEnabled, setRestoreEnabled] = useState(false)
  const [nas, setNas] = useState(null)
  const [current, setCurrent] = useState(null) // { dir, files, count }
  const [loadingBackups, setLoadingBackups] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [backingUp, setBackingUp] = useState(false)
  const [msg, setMsg] = useState(null) // { type: 'ok'|'error'|'info', text }

  const fetchBackups = useCallback(async () => {
    setLoadingBackups(true)
    try {
      const { data } = await api.get('/playlist/backups')
      setBackups(data?.backups ?? [])
      setRestoreEnabled(!!data?.restoreEnabled)
      setNas(data?.nas ?? null)
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.error ?? 'Errore caricamento backup' })
    } finally {
      setLoadingBackups(false)
    }
  }, [])

  useEffect(() => {
    fetchBackups()
  }, [fetchBackups])

  // Legge la NAS solo su richiesta esplicita (niente accessi automatici alla prod)
  const handleRefresh = async () => {
    setRefreshing(true)
    setMsg(null)
    try {
      const { data } = await api.get('/playlist/current')
      setCurrent(data)
      setMsg({ type: 'ok', text: `Lettura NAS ok: ${data.count} file .xml.` })
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.error ?? 'Errore lettura NAS' })
    } finally {
      setRefreshing(false)
    }
  }

  const handleBackup = async () => {
    setBackingUp(true)
    setMsg(null)
    try {
      const { data } = await api.post('/playlist/backup')
      setMsg({ type: 'ok', text: `Backup creato: ${data?.backup?.filesCount ?? 0} file .xml.` })
      await fetchBackups()
    } catch (e) {
      setMsg({ type: 'error', text: e?.response?.data?.error ?? 'Errore durante il backup' })
    } finally {
      setBackingUp(false)
    }
  }

  const handleRestore = async (id) => {
    setMsg(null)
    if (!window.confirm('Ripristinare questo backup sulla NAS?')) return
    try {
      await api.post(`/playlist/backups/${id}/restore`)
      setMsg({ type: 'ok', text: 'Restore eseguito.' })
    } catch (e) {
      const err = e?.response?.data
      if (err?.error === 'restore_disabled') {
        setMsg({ type: 'info', text: err.message ?? 'Restore disabilitato (fase solo lettura).' })
      } else {
        setMsg({ type: 'error', text: err?.error ?? 'Errore restore' })
      }
    }
  }

  const latest = backups[0] ?? null

  return (
    <div className="pl-wrap">
      <div className="pl-header">
        <div>
          <h1 className="pl-title">Backup Playlist</h1>
          <p className="pl-muted">
            Cartella NAS: <code>{nas?.dir ?? '—'}</code>{' '}
            {nas && nas.configured === false && (
              <span className="pl-tag pl-tag--warn">NAS non configurata</span>
            )}
          </p>
        </div>
        <div className="pl-actions">
          <button className="pl-btn pl-btn--secondary" onClick={handleRefresh} disabled={refreshing}>
            <i className="fas fa-sync" /> {refreshing ? 'Lettura…' : 'Aggiorna (leggi NAS)'}
          </button>
          <button className="pl-btn pl-btn--primary" onClick={handleBackup} disabled={backingUp}>
            <i className="fas fa-save" /> {backingUp ? 'Backup…' : 'Backup ora'}
          </button>
        </div>
      </div>

      {msg && <div className={`pl-alert pl-alert--${msg.type}`}>{msg.text}</div>}

      {current && (
        <div className="pl-card">
          <h2 className="pl-card__title">File .xml attuali sulla NAS ({current.count})</h2>
          {current.files.length === 0 ? (
            <div className="pl-empty">Nessun file .xml trovato nella cartella.</div>
          ) : (
            <ul className="pl-filelist">
              {current.files.map((f) => (
                <li key={f}>
                  <i className="fas fa-file-code" /> {f}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="pl-card">
        <h2 className="pl-card__title">Ultimo backup</h2>
        {loadingBackups ? (
          <div className="pl-muted">Caricamento…</div>
        ) : !latest ? (
          <div className="pl-empty">Nessun backup ancora. Premi “Backup ora”.</div>
        ) : (
          <>
            <div className="pl-meta">
              <span>
                <strong>Data:</strong> {fmtDateTime(latest.createdAt)}
              </span>
              <span>
                <strong>File:</strong> {latest.filesCount}
              </span>
              <span>
                <strong>Origine:</strong> {latest.source}
              </span>
              <span className="pl-hash" title={latest.contentHash}>
                <strong>Hash:</strong> {String(latest.contentHash ?? '').slice(0, 12)}…
              </span>
            </div>
            <ul className="pl-filelist">
              {(latest.fileList ?? []).map((f) => (
                <li key={f}>
                  <i className="fas fa-file-code" /> {f}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="pl-card">
        <h2 className="pl-card__title">Backup recenti</h2>
        {!restoreEnabled && (
          <div className="pl-alert pl-alert--info">
            Il ripristino sulla NAS è disabilitato in questa fase (solo lettura).
          </div>
        )}
        {backups.length === 0 ? (
          <div className="pl-empty">—</div>
        ) : (
          <table className="pl-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>File</th>
                <th>Origine</th>
                <th aria-label="azioni" />
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.id}>
                  <td>{fmtDateTime(b.createdAt)}</td>
                  <td>{b.filesCount}</td>
                  <td>{b.source}</td>
                  <td>
                    <button
                      className="pl-btn pl-btn--ghost"
                      onClick={() => handleRestore(b.id)}
                      disabled={!restoreEnabled}
                      title={
                        restoreEnabled
                          ? 'Ripristina sulla NAS'
                          : 'Restore disabilitato (fase solo lettura)'
                      }
                    >
                      <i className="fas fa-undo" /> Ripristina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
